import { BackButton } from "@components/back-button";
import { Link, useNavigate } from "react-router-dom";
import { DocumentViewButton } from "@components/common/common";
import { PrescriptionT } from "@interfaces/prescriptions";
import clsx from "clsx";
import { FC } from "react";
import { NotePencilIcon, TrashIcon, PaperPlaneTiltIcon, CheckCircleIcon, XCircleIcon, SealWarningIcon, SealCheckIcon } from "@phosphor-icons/react";
import { $User, $UserAddPermissions, ACTIONS } from "@store/user-store";
import moment from "moment";
import { Tooltip } from "@mui/material";
import style from "@scss/pages/prescription/prescriptions.module.scss";
import PrescriptionService from "@services/prescription-service";
import { WrapperSVG } from "@components/WrapperSVG/WrapperSVG";
import {
  $Prescriptions,
  removePrescription,
  setPrescriptions,
  setSelectedPrescription,
} from "@store/prescription-store";
import { useUnit } from "effector-react";
import { UserDataT } from "@interfaces/user";
import { useModal } from "@modals/index";
import { useSelectedCompanyQuery } from "@lib/company";
import { useDownloadPrescriptionFileMutation } from "@lib/prescriptions";
import { downloadBlob } from "@utils/downloadBlob";

export type PrescriptionItemProps = {
  prescription: PrescriptionT;
  singleMode: boolean;
};

export const PrescriptionItem: FC<PrescriptionItemProps> = ({
  prescription,
  singleMode,
}) => {
  const user = useUnit($User) as UserDataT;
  const permissions = useUnit($UserAddPermissions);
  const { company } = useSelectedCompanyQuery();
  const { rows, count } = useUnit($Prescriptions);

  const modal = useModal();
  const history = useNavigate();

  const downloadFileMutation = useDownloadPrescriptionFileMutation();

  const handleComplete = (status: boolean, prescriptionId: number) => {
    PrescriptionService.markAsCompleted(
      prescriptionId,
      status,
      (err, res) => {
        if (err) {
          return console.error("Произошла неожиданная ошибка");
        }
        setPrescriptions({
          count,
          rows: rows.map((item) => {
            if (item.id === prescriptionId) {
              item.isComplete = status;
            }
            return item;
          }),
        });
      }
    );
  };

  const handleSend = (prescriptionId: number) => {
    PrescriptionService.send(prescriptionId, (err, res) => {
      if (err || !res) {
      } else {
        modal.open("NotificationModal", {
          modalData: {
            modalTitle: "",
            text: "Предписание отправлено",
          },
          onConfirm: () => {
            modal.close();
            history("/company/prescriptions");
          },
        });
      }
    });
  }

  const handleDelete = (id: number) => {
    PrescriptionService.delete(id, (err, res) => {
      if (err) {
        return console.error("При удалении предписания произошла ошибка");
      }
      removePrescription(id);
    });
  };

  const handleDownload = (fileName: string, originalFileName: string) => {
    if (!company) return;

    downloadFileMutation.mutate(
      {
        companyId: company.id,
        fileName: fileName,
      },
      {
        onSuccess: (blob) => {
          downloadBlob(blob, originalFileName);
        },
      }
    );
  };

  const fileName = `Предписание_#${prescription.id}/${prescription.companyId}.docx`;
  const filePath = `uploads/companies/${company?.id}/prescriptions/${prescription.file}`;

  return (
    <div
      className={clsx(style.prescription_item, {
        [style.single_item]: singleMode,
      })}
    >
      <div className={clsx(style.prescription_item_content)}>
        {singleMode && <BackButton className={style.backButton} />}
        <div className={clsx(style.prescription_item_title)}>
          <Link
            to={`/company/prescriptions/${prescription.id}`}
            onClick={() => setSelectedPrescription(prescription)}
            className={clsx(style.prescription_title_text)}
          >
            Предписание #{prescription.id}/{prescription.companyId}
          </Link>
          <div className={clsx(style.prescription_action_buttons)}>
            <WrapperSVG color={prescription.isComplete ? 'red' : 'green-light'} margin>
              <Tooltip title={prescription.isComplete ? 'Отменить выполнение' : 'Подтвердить выполнение'} placement="top">
                {prescription.isComplete ? (
                  <XCircleIcon
                    size={24}
                    onClick={() => handleComplete(false, prescription.id)}
                  />
                ) : (
                  <CheckCircleIcon
                    size={24}
                    onClick={() => handleComplete(true, prescription.id)}
                  />
                )}
              </Tooltip>
            </WrapperSVG>
            <WrapperSVG color="blue" margin>
              <Tooltip title="Отправить" placement="top">
                <PaperPlaneTiltIcon
                  size={24}
                  onClick={() => 
                    modal.open("NotificationModal", {
                      modalData: {
                        btnText: "Отправить",
                        modalTitle: 'Отправить предписание',
                        text: `Вы уверены, что хотите отправить "Предписание #${prescription.id}/${prescription.companyId}"?`,
                        id: prescription.id,
                      },
                      onConfirm: () => handleSend(prescription.id),
                    })
                  }
                />
              </Tooltip>
            </WrapperSVG>
            {permissions.hasPermission(ACTIONS.companyPrescriptions_allowedToEdit) && (
              <Link
                to={`/company/prescriptions/${prescription.id}/edit`}
                onClick={() => setSelectedPrescription(prescription)}
              >
                <WrapperSVG color="orange" margin>
                  <Tooltip title="Изменить" placement="top">
                    <NotePencilIcon size={24} />
                  </Tooltip>
                </WrapperSVG>
              </Link>
            )}
            {permissions.hasPermission(ACTIONS.companyPrescriptions_allowedToDelete) && (
              <WrapperSVG color="">
                <Tooltip title="Удалить" placement="top">
                  <TrashIcon
                    size={24}
                    onClick={() =>
                      modal.open("ConfirmActionModal", {
                        btnText: "Удалить",
                        modalData: {
                          removeIcon: false,
                          text: `Вы уверены, что хотите удалить предписание "#${prescription.id}/${prescription.companyId}"?`,
                        },
                        onConfirm: () => handleDelete(prescription.id),
                      })
                    }
                  />
                </Tooltip>
              </WrapperSVG>
            )}
          </div>
        </div>
        <Link
          to={`/company/prescriptions/${prescription.id}`}
          onClick={() => setSelectedPrescription(prescription)}
          className={clsx(style.prescription_text_wrapper)}
        >
          <p className={clsx(style.prescription_text)}><b>Кому:</b> {prescription.to_info.name}</p>
          <p className={clsx(style.prescription_text)}><b>Подразделение:</b> {prescription.department_info.title}</p>
          <p className={clsx(style.prescription_text)}><b>Дата о решении выполнения:</b> {prescription.date_accept}</p>
        </Link>
        <p className={clsx(style.prescription_gray_text)}>
          {moment(prescription.createdAt).format("lll")}

          <div className="form-files-list" style={{ width: 'auto' }}>
            <div className="form-file-item" style={{ marginTop: '0px', padding: '0px 5px' }}>
              <div className="flex-n-c">
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ margin: '5px' }}
                >
                  <path
                    d="M8 0C6.9 0 6 0.9 6 2V30C6 31.1 6.9 32 8 32H28C29.1 32 30 31.1 30 30V8L22 0H8Z"
                    fill="#DFE3F1"
                  />
                  <path d="M24 8H30L22 0V6C22 7.1 22.9 8 24 8Z" fill="#B0B7BD" />
                  <path d="M30 14L24 8H30V14Z" fill="#CAD1D8" />
                </svg>
                {fileName}
              </div>
              <div className="flex-n-c">
                <Tooltip title="Редактировать" placement="top">
                  <DocumentViewButton
                    fileName={fileName}
                    filePath={filePath}
                    fileKey={`${prescription.file}_${prescription.companyId}`}
                    modal={modal}
                  />
                </Tooltip>
                <Tooltip title="Скачать" placement="top">
                  <button onClick={() => handleDownload(prescription.file, fileName)}>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className=""
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      style={{ width: "20px", height: "20px", margin: 0 }}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>

          {prescription.send_status ? (
            <WrapperSVG color="green" cursor={false}>
              <Tooltip title="Доставлено" placement="top">
                <SealCheckIcon size={20} />
              </Tooltip>
            </WrapperSVG>
          ) : (
            <WrapperSVG color="red" cursor={false}>
              <Tooltip title="Не доставлено" placement="top">
                <SealWarningIcon size={20} />
              </Tooltip>
            </WrapperSVG>
          )}
        </p>
      </div>
    </div>
  );
};
